import { Link } from 'react-router-dom';
import { consultingServices } from '../../../../shared/consultingServices';
import { ArrowRight, ChevronDown, TrendingUp } from 'lucide-react';
import { useState } from 'react';

export default function ConsultingServicesPage() {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [expandedSubcategory, setExpandedSubcategory] = useState(null);

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
    setExpandedSubcategory(null);
  };

  const toggleSubcategory = (subcategoryId) => {
    setExpandedSubcategory(expandedSubcategory === subcategoryId ? null : subcategoryId);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800 text-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-block bg-primary-500/30 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-sm font-semibold text-primary-100">Strategic & Operational Excellence</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Consulting Services for Long-Term Care Organizations
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              Expert strategic and operational consulting designed to help long-term care organizations achieve sustainable growth, operational efficiency, and strategic clarity in a complex and evolving landscape.
            </p>
          </div>
        </div>
      </div>

      {/* Services Accordion */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="space-y-6">
          {consultingServices.map((category) => (
            <div key={category.id} className="border border-secondary-200 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300">
              {/* Main Category */}
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full bg-gradient-to-r from-primary-50 to-secondary-50 hover:from-primary-100 hover:to-secondary-100 transition-colors duration-300 p-6 text-left flex items-center justify-between"
              >
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-secondary-900 mb-2">
                    {category.label}
                  </h2>
                  <p className="text-secondary-600 text-lg">
                    {category.description}
                  </p>
                </div>
                <ChevronDown
                  size={28}
                  className={`text-primary-600 flex-shrink-0 ml-4 transition-transform duration-300 ${
                    expandedCategory === category.id ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Category Overview */}
              {expandedCategory === category.id && (
                <div className="bg-white border-t border-secondary-200 p-6">
                  <p className="text-secondary-700 leading-relaxed mb-6 text-lg">
                    {category.overview}
                  </p>

                  {/* Subcategories */}
                  <div className="space-y-4">
                    {category.subcategories.map((subcategory, idx) => (
                      <div key={subcategory.id} className="border border-secondary-100 rounded-lg overflow-hidden">
                        {/* Subcategory Button */}
                        <button
                          onClick={() => toggleSubcategory(expandedSubcategory === subcategory.id ? null : subcategory.id)}
                          className="w-full bg-gradient-to-r from-secondary-50 to-primary-50 hover:from-secondary-100 hover:to-primary-100 transition-colors duration-300 p-5 text-left flex items-center justify-between group"
                        >
                          <div className="flex-1">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">
                                {String.fromCharCode(97 + idx)}
                              </div>
                              <div className="flex-1">
                                <h3 className="text-lg font-bold text-secondary-900">
                                  {subcategory.label}
                                </h3>
                                <p className="text-secondary-600 text-sm mt-1">
                                  {subcategory.description}
                                </p>
                              </div>
                            </div>
                          </div>
                          <ChevronDown
                            size={20}
                            className={`text-primary-600 flex-shrink-0 ml-4 transition-transform duration-300 group-hover:text-primary-700 ${
                              expandedSubcategory === subcategory.id ? 'rotate-180' : ''
                            }`}
                          />
                        </button>

                        {/* Subcategory Details */}
                        {expandedSubcategory === subcategory.id && (
                          <div className="bg-white border-t border-secondary-100 p-6 space-y-6">
                            {/* Details */}
                            <div>
                              <p className="text-secondary-700 leading-relaxed">
                                {subcategory.details}
                              </p>
                            </div>

                            {/* Phases (for engagement assessment) */}
                            {subcategory.phases && (
                              <div>
                                <h4 className="font-bold text-secondary-900 mb-4">Five-Phase Approach:</h4>
                                <ul className="space-y-3">
                                  {subcategory.phases.map((phase, phaseIdx) => (
                                    <li key={phaseIdx} className="flex items-start gap-3">
                                      <span className="text-primary-600 font-bold mt-1">•</span>
                                      <span className="text-secondary-700">{phase}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Deliverables (for burnout assessment) */}
                            {subcategory.deliverables && (
                              <div>
                                <h4 className="font-bold text-secondary-900 mb-4">Deliverables:</h4>
                                <ul className="space-y-3">
                                  {subcategory.deliverables.map((deliverable, delIdx) => (
                                    <li key={delIdx} className="flex items-start gap-3">
                                      <span className="text-primary-600 font-bold mt-1">•</span>
                                      <span className="text-secondary-700">{deliverable}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Process (for retention strategy) */}
                            {subcategory.process && (
                              <div>
                                <h4 className="font-bold text-secondary-900 mb-4">Our Process:</h4>
                                <ul className="space-y-3">
                                  {subcategory.process.map((step, stepIdx) => (
                                    <li key={stepIdx} className="flex items-start gap-3">
                                      <span className="text-primary-600 font-bold mt-1">•</span>
                                      <span className="text-secondary-700">{step}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Outcome */}
                            <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-lg p-6 border-l-4 border-primary-600">
                              <h4 className="font-bold text-secondary-900 mb-2">Expected Outcome:</h4>
                              <p className="text-secondary-700 leading-relaxed">
                                {subcategory.outcome}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="bg-gradient-to-br from-secondary-900 to-secondary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">Why Choose Stratel?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Industry Experience',
                description: 'Our team brings experience in healthcare strategy, operations, and compliance.',
              },
              {
                title: 'Outcome-Focused Support',
                description: 'We help organizations work toward clearer priorities, stronger operations, and better organizational health.',
              },
              {
                title: 'Partnership Approach',
                description: 'We work as an extension of your team, building capacity and empowering your staff for long-term success.',
              },
            ].map((item, idx) => (
              <div key={idx} className="bg-secondary-800/50 backdrop-blur-sm border border-secondary-700 rounded-xl p-8">
                <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                <p className="text-secondary-300 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-12 md:p-16 text-center text-white shadow-xl">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Drive Change?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto leading-relaxed">
            Let's schedule a conversation about your strategic objectives and how Stratel can help you achieve sustainable growth and operational excellence.
          </p>
          <Link 
            to="/get-started"
            className="inline-flex items-center justify-center bg-white text-primary-700 px-8 py-4 rounded-lg font-bold hover:bg-primary-50 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Get Started
            <ArrowRight size={20} className="ml-2" />
          </Link>
        </div>
      </div>
    </div>
  );
}
